import React, { useRef, useCallback, useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Home, LayoutDashboard } from 'lucide-react';
import { doc, getDoc, collection, query, where, limit, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import ChooseArtworkModal, { getTypeOfArtworkFromKey } from '../components/ChooseArtworkModal';
import KidBackground from '../ui/backgrounds/KidBackground';
import splashImage from '../assets/splash.png';
import { getArtworkCards } from '../utils/artHistoryFlowService';
import RoomSceneWrapper from '../components/artHistoryRoom/RoomSceneWrapper';
import PerspectiveHero from '../components/artHistoryRoom/PerspectiveHero';
import { resolveImageUrl, warmResolvedImageUrls } from '../utils/urlResolver';
import { backendConfig } from '../config/backendConfig';
import '../styles/learnArtHistory.css';
import {
  readStoredLevel,
  writeStoredLevel,
  readTotalsCache,
  writeTotalsCache,
  readLevelPreviewCache,
  writeLevelPreviewCache,
  readCardSnapshot,
  writeCardSnapshot,
  readCompletedCache,
  writeCompletedCache,
  hydrateCardsForLevel,
  warmCategoryCardImages,
  isStableVerifiedPreviewUrl,
  shouldRevalidateLevel,
  writeLevelBundle,
} from '../utils/learnArtHistoryCache';

/** Start Learn Art History flow: open "Choose today's artwork" modal instead of navigating. */
export function startLearnArtHistoryFlow({ artworkTypeKey, level, artworkTypeLabel }) {
  return { artworkTypeKey, level, artworkTypeLabel };
}

// Artwork types from Admin Artworks (AdminArtworks.js) — same list so cards reflect admin types on all levels.
const ADMIN_ARTWORK_TYPES = [
  'Portrait', 'Landscape', 'Still Life', 'Abstract', 'Sacred',
  'Genre Scene', 'Self-Portrait', 'Architectural', 'Animal/Birds', 'Fantasy',
  'Study/Sketch', 'Miniature', 'Calligraphy', 'Sculpture', 'Interior',
  'Seascape', 'Floral', 'Objects',
];

// Slug from label for routing (e.g. "Still Life" -> "still-life", "Animal/Birds" -> "animal-birds")
function labelToKey(label) {
  return label.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-');
}

// What kids will learn per artwork type (kid-friendly). Optional: add per-level copy later.
const LEARN_BY_TYPE = {
  'Portrait': 'Learn how artists show a person\'s face and mood. Spot expressions, light, and why portraits were important in history.',
  'Landscape': 'Discover how artists paint nature: skies, mountains, and seasons. See how light and weather change the scene.',
  'Still Life': 'Explore paintings of everyday objects like fruit and flowers. Learn about shapes, colours, and composition.',
  'Abstract': 'Find out how art can show feelings and ideas without painting real things. Play with shapes and colours.',
  'Sacred': 'See how art tells religious and spiritual stories from different cultures and times.',
  'Genre Scene': 'Look at art that shows people doing everyday life—work, play, and celebrations from the past.',
  'Self-Portrait': 'See how artists paint themselves. Learn about identity and how we show who we are.',
  'Architectural': 'Discover buildings and structures in art. Learn about design, perspective, and famous places.',
  'Animal/Birds': 'Explore how artists paint animals and birds. Learn about nature, movement, and symbolism.',
  'Fantasy': 'Enter imaginary worlds! See how artists invent creatures, places, and magical stories.',
  'Study/Sketch': 'Peek at artists\' practice drawings. Learn how ideas start with sketches before big paintings.',
  'Miniature': 'Look at tiny, detailed art. Discover patience and skill in small-scale masterpieces.',
  'Calligraphy': 'See beautiful writing as art. Learn about letters, rhythm, and different scripts from around the world.',
  'Sculpture': 'Explore art you can walk around. Learn about form, materials, and how artists work in 3D.',
  'Interior': 'Step inside rooms in art. See how artists paint space, furniture, and light indoors.',
  'Seascape': 'Discover oceans, ships, and coastlines in art. Learn about water, weather, and sea stories.',
  'Floral': 'Explore flowers in art. Learn about colour, pattern, and what flowers meant to different cultures.',
  'Objects': 'Look at art that focuses on single objects. Learn about meaning, symbolism, and still life.',
};

// Local folder for card images: place files in public/learn-art-history/ (see README there for filenames).
const VERIFIED_STORAGE_PREFIX = 'verified-artworks/';
const VERIFIED_MEDIA_BUCKET = 'artmosphere-89b64.firebasestorage.app';
const LOCAL_CARD_IMAGE_BASE = '/learn-art-history';
/** Background image warm limit — never blocks painting cards. */
const CARD_PRELOAD_BACKGROUND_LIMIT = 12;

function buildVerifiedMediaUrlFromPath(storagePath = '') {
  const p = String(storagePath || '').trim();
  if (!p.startsWith(VERIFIED_STORAGE_PREFIX)) return '';
  return `https://firebasestorage.googleapis.com/v0/b/${VERIFIED_MEDIA_BUCKET}/o/${encodeURIComponent(p)}?alt=media`;
}

function getRowImageCandidates(row = {}) {
  const verified = String(row?.verifiedImageUrl || '').trim();
  const storagePath = String(row?.verifiedImageStoragePath || '').trim();
  const media = buildVerifiedMediaUrlFromPath(storagePath);
  return [
    verified,
    media,
  ].filter(Boolean);
}

function toStablePreviewUrl(rawUrl = '') {
  const url = String(rawUrl || '').trim();
  if (!/^https?:\/\//i.test(url)) return url;
  if (url.includes('/proxy-image?url=')) return url;
  if (url.includes('firebasestorage.googleapis.com') || url.includes('storage.googleapis.com')) return url;
  return `${backendConfig.baseUrl}/proxy-image?url=${encodeURIComponent(url)}`;
}

// Build full list: each admin type appears for all levels. imageUrl is set from first stored artwork per type (or local fallback).
function buildArtHistoryTypes() {
  return ADMIN_ARTWORK_TYPES.map((label) => {
    const key = labelToKey(label);
    return {
      key,
      label,
      learnCopy: LEARN_BY_TYPE[label] || `Explore ${label} and discover what artists teach us.`,
      imageUrl: '',
      fallbackImageUrl: `${LOCAL_CARD_IMAGE_BASE}/${key}.jpg`,
    };
  });
}

// Fetch first stored artwork image URL per type (and level) from artHistoryArtworks; returns map of type key -> imageUrl.
function isStrictVerifiedBucketRecord(data = {}) {
  const path = typeof data.verifiedImageStoragePath === 'string' ? data.verifiedImageStoragePath : '';
  const strictApproved = data.verifiedStrictCommercialApproved === true;
  const hasVerifiedPath = path.startsWith(VERIFIED_STORAGE_PREFIX);
  return strictApproved && hasVerifiedPath;
}

async function fetchFirstImagePerType(level, onlyLabels = []) {
  const levelType = level != null ? `Level ${level}` : 'Level 1';
  const keyToUrl = {};
  const labels = Array.isArray(onlyLabels) && onlyLabels.length > 0 ? onlyLabels : ADMIN_ARTWORK_TYPES;
  await Promise.all(
    labels.map(async (label) => {
      try {
        const labelVariants = label === 'Floral'
          ? ['Floral', 'Flower painting', 'Flower Painting']
          : label === 'Animal/Birds'
            ? ['Animal/Birds', 'Animal', 'Animals', 'Animal / Birds']
            : [label];
        // Per-variant query to strictly stay in the same bucket (type + level + strict-approved).
        const snaps = await Promise.all(
          labelVariants.map((lab) =>
            getDocs(
              query(
                collection(db, 'artHistoryArtworks'),
                where('typeOfArtwork', '==', lab),
                where('levelType', '==', levelType),
                where('verifiedStrictCommercialApproved', '==', true),
                limit(12)
              )
            )
          )
        );
        const rows = snaps.flatMap((snap) => snap.docs.map((d) => d.data()));
        const strictRow = rows.find((data) => isStrictVerifiedBucketRecord(data));
        const relaxedRow = rows.find((data) => {
          const verifiedLike = data?.verifiedStrictCommercialApproved === true;
          const hasAnyImage =
            !!String(data?.verifiedImageUrl || '').trim() ||
            !!String(data?.imageUrl || '').trim() ||
            !!String(data?.originalImageUrl || '').trim() ||
            !!String(data?.coverUrl || '').trim();
          return verifiedLike && hasAnyImage;
        });
        const orderedRows = strictRow
          ? [strictRow, ...rows.filter((r) => r !== strictRow)]
          : relaxedRow
            ? [relaxedRow, ...rows.filter((r) => r !== relaxedRow)]
            : rows;

        // Stable rule: choose first VERIFIED-STABLE candidate deterministically.
        // Do not gate assignment on runtime probe timing.
        let selected = '';
        for (const row of orderedRows.slice(0, 24)) {
          const candidates = getRowImageCandidates(row);
          const firstStable = candidates.find((u) => isStableVerifiedPreviewUrl(u));
          if (firstStable) {
            selected = firstStable;
            break;
          }
        }
        if (selected) {
          keyToUrl[labelToKey(label)] = selected;
        }
      } catch (_) {}
    })
  );
  return keyToUrl;
}

function getLabelVariants(label) {
  if (label === 'Floral') return ['Floral', 'Flower painting', 'Flower Painting'];
  if (label === 'Animal/Birds') return ['Animal/Birds', 'Animal', 'Animals', 'Animal / Birds'];
  return [label];
}

/** Fast aggregate counts via Firestore count API (avoids downloading every doc on first load). */
async function fetchArtworkCountsByType(level) {
  const levelType = level != null ? `Level ${level}` : 'Level 1';
  const counts = {};
  try {
    await Promise.all(
      ADMIN_ARTWORK_TYPES.map(async (label) => {
        try {
          const variants = getLabelVariants(label);
          const variantCounts = await Promise.all(
            variants.map(async (lab) => {
              const snap = await getCountFromServer(
                query(
                  collection(db, 'artHistoryArtworks'),
                  where('typeOfArtwork', '==', lab),
                  where('levelType', '==', levelType),
                  where('verifiedStrictCommercialApproved', '==', true)
                )
              );
              return snap.data().count || 0;
            })
          );
          counts[label] = Math.max(0, ...variantCounts);
        } catch (_) {
          counts[label] = 0;
        }
      })
    );
  } catch (err) {
    console.warn('[LearnArtHistory] Failed to fetch artwork counts by type:', err);
    ADMIN_ARTWORK_TYPES.forEach((lab) => {
      if (!(lab in counts)) counts[lab] = 0;
    });
  }
  return counts;
}

const DEFAULT_ART_HISTORY_TYPES = buildArtHistoryTypes();

function isRemoteUrl(url = '') {
  return /^https?:\/\//i.test(url);
}

// FlipCard-style shiny border (same as My Artwork / DashboardLanding cards)
const shinyBorderShadow = '0 0 0 2px rgba(255, 248, 220, 0.95), 0 0 0 3px rgba(255, 213, 79, 0.4), 0 0 28px rgba(255, 193, 7, 0.45), 0 12px 32px rgba(255, 193, 7, 0.25)';
const goldFrameStyle = {
  background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 25%, #d4af37 50%, #f4d03f 75%, #d4af37 100%)',
  boxShadow: 'inset 0 0 10px rgba(212, 175, 55, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)',
};

function CategoryCard({ item, selectedLevel, completed = 0, total = 0, isFlipped, onFlipOpen, onClick }) {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const imageUrl = String(item.imageUrl || '').trim();
  const hardFallbackUrl = splashImage;
  const [currentSrc, setCurrentSrc] = useState(imageUrl || hardFallbackUrl);

  React.useEffect(() => {
    const target = imageUrl || hardFallbackUrl;
    if (!target) return;
    // Keep current frame visible; swap only once next image is fully loaded.
    let cancelled = false;
    try {
      const probe = new Image();
      probe.decoding = 'async';
      probe.onload = () => {
        if (cancelled) return;
        setCurrentSrc(target);
      };
      probe.onerror = () => {
        if (cancelled) return;
        setCurrentSrc(hardFallbackUrl);
      };
      probe.src = target;
    } catch (_) {
      setCurrentSrc(hardFallbackUrl);
    }
    return () => {
      cancelled = true;
    };
  }, [imageUrl, hardFallbackUrl]);

  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);

  const faceShadow = isHovered ? shinyBorderShadow : goldFrameStyle.boxShadow;
  const flipTransition = 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)';

  return (
    <div
      ref={cardRef}
      className="learn-art-history-card flex-shrink-0 w-full max-w-full md:w-[320px] md:max-w-[320px] min-h-[360px] md:min-h-[468px] rounded-2xl overflow-visible cursor-pointer select-none"
      style={{ perspective: '1100px' }}
      data-card-mode="flip-3d"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isFlipped) {
          onFlipOpen();
          return;
        }
        onClick();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!isFlipped) {
            onFlipOpen();
          } else {
            onClick();
          }
        }
      }}
    >
      <div
        className="relative w-full min-h-[468px] rounded-2xl origin-center"
        style={{
          transformStyle: 'preserve-3d',
          WebkitTransformStyle: 'preserve-3d',
          transform: isFlipped
            ? 'rotateY(180deg) scale(1.06) translateY(-10px)'
            : 'rotateY(0deg) scale(1) translateY(0)',
          transition: flipTransition,
        }}
      >
        {/* Front — artwork + progress; translateZ + no overflow on this layer fixes WebKit backface / mirrored badge */}
        <div
          className="absolute inset-0 rounded-2xl p-[6px] overflow-visible"
          style={{
            ...goldFrameStyle,
            transform: 'translateZ(4px)',
            transformStyle: 'preserve-3d',
            WebkitTransformStyle: 'preserve-3d',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            boxShadow: faceShadow,
            transition: 'box-shadow 0.3s ease, opacity 0.18s ease',
            // When flipped, hide front entirely — avoids compositor bugs where z-index children leak through as mirrored text
            opacity: isFlipped ? 0 : 1,
            visibility: isFlipped ? 'hidden' : 'visible',
            pointerEvents: isFlipped ? 'none' : 'auto',
          }}
        >
          <div className="w-full h-full min-h-0 bg-white flex flex-col rounded-[14px] overflow-hidden">
            <div className="relative flex-shrink-0 h-[min(280px,42vh)] min-h-[220px] overflow-hidden bg-gradient-to-br from-amber-50 via-amber-50/90 to-amber-100/70">
              <img
                src={currentSrc}
                alt=""
                loading="eager"
                fetchpriority="high"
                decoding="sync"
                className="absolute inset-0 w-full h-full object-cover object-center"
                onError={() => {
                  if (currentSrc !== hardFallbackUrl) {
                    setCurrentSrc(hardFallbackUrl);
                  }
                }}
              />
              <span
                className="absolute top-3 right-3 px-2.5 py-1 rounded-lg bg-amber-200/90 text-amber-900 text-xs font-semibold shadow-sm"
                style={{
                  transform: 'translateZ(2px)',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                Level {selectedLevel}
              </span>
            </div>
            <div className="flex-shrink-0 p-4 pt-3 pb-3.5 border-t border-amber-100 bg-white">
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-amber-800/90 mb-0.5 leading-normal">
                Artwork category
              </span>
              <span className="font-bold text-gray-800 text-lg leading-snug block">{item.label}</span>
              <div className="mt-2 space-y-1.5">
                <div className="w-full h-1.5 bg-amber-100 rounded-full overflow-hidden flex-shrink-0">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all duration-300"
                    style={{ width: total > 0 ? `${Math.min(100, (completed / total) * 100)}%` : '0%' }}
                  />
                </div>
                <p className="text-[11px] text-amber-800 font-medium leading-normal pb-0.5">
                  {completed} of {total} artworks explored
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Back — counter-rotated so copy stays readable when the inner rotates 180° */}
        <div
          className="absolute inset-0 rounded-2xl p-[6px] overflow-visible"
          style={{
            ...goldFrameStyle,
            transform: 'rotateY(180deg) translateZ(4px)',
            transformStyle: 'preserve-3d',
            WebkitTransformStyle: 'preserve-3d',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            boxShadow: isFlipped
              ? isHovered
                ? shinyBorderShadow
                : '0 20px 40px rgba(0,0,0,0.12), 0 8px 24px rgba(255, 193, 7, 0.25)'
              : goldFrameStyle.boxShadow,
            background: 'rgba(255, 253, 247, 0.98)',
            border: '2px solid rgba(255, 224, 130, 0.6)',
            transition: 'box-shadow 0.3s ease',
          }}
        >
          <div className="w-full h-full flex flex-col p-4 overflow-hidden rounded-[14px] bg-white">
            <h3 className="font-bold text-gray-800 text-base mb-2 leading-tight flex-shrink-0">What you&apos;ll learn</h3>
            <p className="text-sm text-gray-600 leading-snug flex-1 overflow-y-auto min-h-0 mb-3">
              {item.learnCopy}
            </p>
            <div className="flex-shrink-0 pt-2 flex flex-col gap-2">
              <span className="text-xs text-amber-700 font-medium">Level {selectedLevel}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onClick();
                }}
                className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-1"
                style={{
                  background: 'linear-gradient(180deg, #FFE082 0%, #FFD54F 100%)',
                  boxShadow: '0 2px 8px rgba(255, 193, 7, 0.35)',
                }}
              >
                Enter Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Backend: Firestore doc config/learnArtHistory with field imageUrls (map of type key -> image URL).
// Example: { imageUrls: { portrait: "https://...", landscape: "https://...", "still-life": "https://...", ... } }
// Upload one full-scale image URL per card type; front of each card will show it with object-cover.
const LEARN_ART_HISTORY_CONFIG_PATH = 'learnArtHistory';
const LEARN_ART_HISTORY_LEVEL_PREVIEWS_PATH = 'learnArtHistoryLevelPreviews';

function LearnArtHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { type } = useParams();
  const scrollRef = useRef(null);
  const initialLevel = readStoredLevel();
  const initialPreviews = readLevelPreviewCache();
  const [selectedLevel, setSelectedLevel] = useState(initialLevel);
  const [levelPreviewImageUrls, setLevelPreviewImageUrls] = useState(initialPreviews);
  const [cardTypes, setCardTypes] = useState(() =>
    hydrateCardsForLevel(initialLevel, DEFAULT_ART_HISTORY_TYPES, initialPreviews, {})
  );
  const [chooseModal, setChooseModal] = useState({ open: false, artworkTypeKey: null, level: 1, artworkTypeLabel: '' });
  /** At most one category card shows its back; opening another card clears the previous flip. */
  const [flippedCategoryKey, setFlippedCategoryKey] = useState(null);
  const [configImageUrls, setConfigImageUrls] = useState({});
  const [totalByType, setTotalByType] = useState(() => readTotalsCache(initialLevel));
  const [completedByType, setCompletedByType] = useState({});

  useEffect(() => {
    writeStoredLevel(selectedLevel);
    setFlippedCategoryKey(null);
  }, [selectedLevel]);

  // Instant paint when switching levels — cached snapshots + totals before network.
  useLayoutEffect(() => {
    const cachedTotals = readTotalsCache(selectedLevel);
    if (cachedTotals && Object.keys(cachedTotals).length > 0) {
      setTotalByType(cachedTotals);
    }
    if (user?.uid) {
      const cachedCompleted = readCompletedCache(user.uid, selectedLevel);
      if (cachedCompleted && Object.keys(cachedCompleted).length > 0) {
        setCompletedByType(cachedCompleted);
      }
    }
    setCardTypes((prev) =>
      hydrateCardsForLevel(selectedLevel, DEFAULT_ART_HISTORY_TYPES, levelPreviewImageUrls, configImageUrls)
    );
    try {
      const saved = readCardSnapshot(selectedLevel);
      const urls = (saved && Array.isArray(saved) ? saved.map((x) => x?.imageUrl) : []).filter(Boolean);
      warmCategoryCardImages(
        urls.map((imageUrl) => ({ imageUrl: resolveImageUrl(imageUrl) || imageUrl })),
        CARD_PRELOAD_BACKGROUND_LIMIT
      );
      warmResolvedImageUrls(urls, CARD_PRELOAD_BACKGROUND_LIMIT).catch(() => {});
    } catch (_) {}
  }, [selectedLevel, levelPreviewImageUrls, configImageUrls, user?.uid]);

  // Back from flow: align level strip with the artwork’s level (session can be stale on deep links).
  const restoreLevelFromFlow = location.state?.restoreLevel;
  useEffect(() => {
    if (restoreLevelFromFlow === undefined || restoreLevelFromFlow === null || restoreLevelFromFlow === '') return;
    const n =
      typeof restoreLevelFromFlow === 'number'
        ? restoreLevelFromFlow
        : parseInt(String(restoreLevelFromFlow), 10);
    if (Number.isNaN(n) || n < 1 || n > 3) return;
    setSelectedLevel(n);
    writeStoredLevel(n);
    navigate('/learn-art-history', { replace: true, state: {} });
  }, [restoreLevelFromFlow, navigate]);

  // Load optional config override once (global per-type image URLs)
  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'config', LEARN_ART_HISTORY_CONFIG_PATH))
      .then((configSnap) => {
        if (cancelled || !configSnap.exists()) return;
        const data = configSnap.data();
        const imageUrls = data?.imageUrls || {};
        if (typeof imageUrls === 'object' && Object.keys(imageUrls).length > 0) setConfigImageUrls(imageUrls);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Load precomputed preview URLs per level (single Firestore doc read).
  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'config', LEARN_ART_HISTORY_LEVEL_PREVIEWS_PATH))
      .then((configSnap) => {
        if (cancelled || !configSnap.exists()) return;
        const data = configSnap.data();
        const byLevel = data?.byLevel || {};
        if (byLevel && typeof byLevel === 'object') {
          setLevelPreviewImageUrls(byLevel);
          writeLevelPreviewCache(byLevel);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Background revalidate: counts + preview URLs (never block first paint on image preload).
  useEffect(() => {
    let cancelled = false;
    const levelPreviewMap =
      levelPreviewImageUrls?.[String(selectedLevel)] || levelPreviewImageUrls?.[selectedLevel] || {};
    const hasLevelPreviews =
      levelPreviewMap && typeof levelPreviewMap === 'object' && Object.keys(levelPreviewMap).length > 0;
    const bundleFresh = !shouldRevalidateLevel(selectedLevel);

    const run = async () => {
      try {
        const countsPromise = fetchArtworkCountsByType(selectedLevel);
        let firstImageByType = {};
        if (!bundleFresh && !hasLevelPreviews) {
          firstImageByType = await fetchFirstImagePerType(selectedLevel);
        }
        const countsByType = await countsPromise;
        if (cancelled) return;

        setTotalByType(countsByType);
        writeTotalsCache(selectedLevel, countsByType);

        if (!bundleFresh) {
          const missingLabels = [];
          DEFAULT_ART_HISTORY_TYPES.forEach((label) => {
            const key = labelToKey(label);
            const previewByLevel = toStablePreviewUrl(levelPreviewMap[key]);
            if (!isStableVerifiedPreviewUrl(previewByLevel)) missingLabels.push(label);
          });

          if (hasLevelPreviews && missingLabels.length > 0) {
            const fetchedMissing = await fetchFirstImagePerType(selectedLevel, missingLabels);
            firstImageByType = { ...firstImageByType, ...fetchedMissing };
          }

          const candidateByKey = {};
          DEFAULT_ART_HISTORY_TYPES.forEach((label) => {
            const key = labelToKey(label);
            const previewByLevel = toStablePreviewUrl(levelPreviewMap[key]);
            const fetchedCandidate = firstImageByType[key] || '';
            candidateByKey[key] = isStableVerifiedPreviewUrl(previewByLevel)
              ? previewByLevel
              : fetchedCandidate;
          });

          const syncCandidateByKey = Object.fromEntries(
            Object.entries(candidateByKey).map(([key, raw]) => {
              const value = raw || '';
              if (!value || !/^https?:\/\//i.test(value)) return [key, ''];
              try {
                const resolved = resolveImageUrl(value) || value;
                if (!isStableVerifiedPreviewUrl(resolved)) return [key, ''];
                return [key, resolved];
              } catch (_) {
                return [key, value];
              }
            })
          );

          setCardTypes((prev) => {
            const next = prev.map((card) => {
              const candidate = syncCandidateByKey[card.key] || '';
              const prevUrl = card.imageUrl || '';
              const nextUrl = candidate || prevUrl;
              if (prevUrl === nextUrl) return card;
              return { ...card, imageUrl: nextUrl };
            });
            writeCardSnapshot(
              selectedLevel,
              next.map(({ key, imageUrl }) => ({ key, imageUrl }))
            );
            warmCategoryCardImages(next, CARD_PRELOAD_BACKGROUND_LIMIT);
            return next;
          });

          writeLevelBundle(selectedLevel, {
            totals: countsByType || readTotalsCache(selectedLevel),
            hasLevelPreviews,
          });
        }
      } catch (err) {
        if (!cancelled) console.warn('[LearnArtHistory] Failed to load card images:', err);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedLevel, configImageUrls, levelPreviewImageUrls]);

  // Premium feel: prewarm adjacent level preview URLs so level switches feel instant.
  useEffect(() => {
    try {
      const adjacent = [selectedLevel - 1, selectedLevel + 1].filter((l) => l >= 1 && l <= 3);
      const urls = [];
      adjacent.forEach((level) => {
        const m = levelPreviewImageUrls?.[String(level)] || levelPreviewImageUrls?.[level] || {};
        Object.values(m || {}).forEach((raw) => {
          const u = resolveImageUrl(toStablePreviewUrl(raw));
          if (isStableVerifiedPreviewUrl(u)) urls.push(u);
        });
      });
      Array.from(new Set(urls)).slice(0, 24).forEach((u) => {
        try {
          const img = new Image();
          img.decoding = 'async';
          img.src = u;
        } catch (_) {}
      });
    } catch (_) {}
  }, [selectedLevel, levelPreviewImageUrls]);

  // Load user completion counts per type *and level* (based on artHistoryFlows cards)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!user?.uid) {
        return;
      }
      const cached = readCompletedCache(user.uid, selectedLevel);
      if (cached && Object.keys(cached).length > 0) {
        setCompletedByType(cached);
      }
      try {
        const cards = await getArtworkCards(user.uid);
        if (cancelled) return;
        const map = {};
        cards.forEach((card) => {
          const typeLabel = card.artworkTypeLabel || '';
          const level = card.level;
          if (!typeLabel || level == null) return;
          if (level !== selectedLevel) return;
          map[typeLabel] = (map[typeLabel] || 0) + 1;
        });
        setCompletedByType(map);
        writeCompletedCache(user.uid, selectedLevel, map);
      } catch (err) {
        if (!cancelled) {
          console.warn('[LearnArtHistory] Failed to load completion counts:', err);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedLevel, user?.uid]);

  const displayedTypes = React.useMemo(() => {
    return [...cardTypes].sort((a, b) => {
      const totalA = totalByType[a.label] || 0;
      const totalB = totalByType[b.label] || 0;
      if (totalB !== totalA) return totalB - totalA;
      return a.label.localeCompare(b.label);
    });
  }, [cardTypes, totalByType]);

  // Prewarm first visible card images to avoid broken icon flash on first paint.
  useEffect(() => {
    const topUrls = displayedTypes
      .slice(0, 12)
      .map((item) => item.imageUrl)
      .filter(Boolean);
    warmResolvedImageUrls(topUrls, 12).catch(() => {});
  }, [displayedTypes]);

  // When we land with state.openModalForType (after redirect from /learn-art-history/:type), open the artwork modal
  const openModalForType = location.state?.openModalForType;
  useEffect(() => {
    if (!openModalForType) return;
    const item = cardTypes.find((t) => t.key === openModalForType);
    if (item) {
      setChooseModal({
        open: true,
        artworkTypeKey: item.key,
        level: selectedLevel,
        artworkTypeLabel: item.label,
      });
    }
    navigate('/learn-art-history', { replace: true, state: {} });
  }, [openModalForType]);

  // Always show the grid; modal opens from card click or from openModalForType state above
  return (
    <RoomSceneWrapper>
      {/* Midground painterly accents inside the room */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
        <img
          src={splashImage}
          alt=""
          className="absolute top-10 right-5 opacity-20"
          style={{
            width: '400px',
            height: '200px',
            objectFit: 'cover',
            transform: 'rotate(15deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(2px)',
          }}
        />
        <img
          src={splashImage}
          alt=""
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15"
          style={{
            width: '300px',
            height: '150px',
            objectFit: 'cover',
            transform: 'translate(-50%, -50%) rotate(10deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(3px)',
          }}
        />
      </div>

      <KidBackground variant="blobs" intensity={0.45} />

      {/* Front content layer: this sits closest to the user */}
      <div className="relative z-10 w-full max-w-full py-0 md:py-4 flex flex-col min-h-0 flex-1 overflow-hidden md:overflow-visible">
      <div className="flex-shrink-0 flex justify-end px-3 md:px-6 pt-2 pb-1">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => navigate('/my-art-world')}
            className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center"
            title="Artist Corner"
          >
            <LayoutDashboard className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
            <span className="hidden md:inline ml-1">Artist Corner</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center"
            title="Home"
          >
            <Home className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
            <span className="hidden md:inline ml-1">Home</span>
          </button>
        </div>
      </div>
      <div className="flex-shrink-0 px-2 md:px-4 pt-1 pb-3">
        <PerspectiveHero
          title="Step Into the Story of Art"
          subtitle="Choose your level, enter a gallery, and explore masterpieces through stories, clues, and kid-friendly discovery."
        >
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3].map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setSelectedLevel(level)}
                  className="min-h-[36px] px-4 py-2 rounded-full text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
                  style={{
                    background: selectedLevel === level
                      ? 'linear-gradient(180deg, #FFE082 0%, #FFD54F 100%)'
                      : 'rgba(255,255,255,0.85)',
                    color: selectedLevel === level ? '#1f2937' : '#92400e',
                    border: `1px solid ${selectedLevel === level ? 'rgba(255, 193, 7, 0.7)' : 'rgba(245, 158, 11, 0.35)'}`,
                    boxShadow: selectedLevel === level ? '0 6px 16px rgba(255, 193, 7, 0.25)' : '0 2px 8px rgba(0,0,0,0.06)',
                  }}
                >
                  Level {level}
                </button>
              ))}
            </div>
          </div>
        </PerspectiveHero>
      </div>

      <div className="learn-art-history-cards-wrap flex-1 px-2 md:px-8 pb-4 md:pb-8 min-h-0 overflow-hidden md:overflow-visible">
        {/* Slight perspective makes cards feel placed on a room floor plane (desktop) */}
        <div className="min-h-0 flex-1 md:flex-none h-full" style={{ perspective: '1200px' }}>
          <div
            ref={scrollRef}
            className="learn-art-history-scroll flex flex-col gap-4 overflow-y-auto overflow-x-hidden items-stretch flex-1 min-h-0 rounded-2xl border border-white/70 bg-white/72 px-3 py-4 shadow-[0_20px_48px_rgba(64,43,15,0.18)] backdrop-blur-sm md:flex-row md:gap-6 md:overflow-x-auto md:overflow-y-visible md:flex-none md:rounded-3xl md:pl-4 md:pr-8 md:pt-14 md:pb-10"
            style={{ scrollBehavior: 'smooth' }}
          >
            {displayedTypes.map((item) => {
              const total = totalByType[item.label] || 0;
              const completed = completedByType[item.label] || 0;
              return (
                <CategoryCard
                  key={item.key}
                  item={item}
                  selectedLevel={selectedLevel}
                  completed={completed}
                  total={total}
                  isFlipped={flippedCategoryKey === item.key}
                  onFlipOpen={() => setFlippedCategoryKey(item.key)}
                  onClick={() => {
                    setFlippedCategoryKey(null);
                    setChooseModal({
                      open: true,
                      artworkTypeKey: item.key,
                      level: selectedLevel,
                      artworkTypeLabel: item.label,
                    });
                  }}
                />
              );
            })}
          </div>
        </div>

      </div>

      <ChooseArtworkModal
        isOpen={chooseModal.open}
        onClose={() => {
          setFlippedCategoryKey(null);
          setChooseModal((p) => ({ ...p, open: false }));
        }}
        artworkTypeKey={chooseModal.artworkTypeKey}
        level={chooseModal.level}
        artworkTypeLabel={chooseModal.artworkTypeLabel}
        onLoadedCount={(count) => {
          const typeLabel = chooseModal.artworkTypeLabel || getTypeOfArtworkFromKey(chooseModal.artworkTypeKey);
          if (!typeLabel) return;
          setTotalByType((prev) => {
            if ((prev?.[typeLabel] || 0) === count) return prev;
            return { ...prev, [typeLabel]: count };
          });
        }}
        onSelectAndStart={(primaryArtwork) => {
          setChooseModal((p) => ({ ...p, open: false }));
          navigate('/learn-art-history/flow', {
            state: {
              artwork: primaryArtwork,
              primaryArtwork,
              artworkTypeKey: chooseModal.artworkTypeKey,
              level: chooseModal.level,
              artworkTypeLabel: chooseModal.artworkTypeLabel,
              openModal: false,
              embeddedInFlow: true,
              fromLearnArtHistory: true,
            },
          });
        }}
      />
      </div>
    </RoomSceneWrapper>
  );
}

export default LearnArtHistory;
